import * as THREE from "three";
import { defineModel, type Part, type Vec3 } from "../../lib";

// Aston Martin DB5 (1963) — the archetypal British grand-tourer coupe (the James
// Bond car) at true scale (1 unit = 1 metre): length ~4.57, width 1.68, height
// 1.32 (low), wheelbase 2.49 (front axle z=+1.05, rear z=-1.44), on chrome wire
// wheels (r ~0.33). Nose is +Z. A LONG, LOW, voluptuous silver coupe whose whole
// side is ONE continuous low line: a long curved bonnet with a subtle power bulge
// and scoop hint, a curved wraparound windscreen, a gently curved formal roof
// that flows in an unbroken fastback down to a short, kicked-up (Kamm-ish) tail —
// a CLOSED body-colour roof over dark glass, never an open deck. The signature
// cues: the iconic wide, low, rounded trapezoidal Aston grille (wider at top,
// chrome-rimmed, fine horizontal chrome slats), faired-in covered headlamps under
// glass on the rounded front wings, three chrome side strakes on each front wing
// behind the wheel, chrome wire wheels with knock-off spinners, slim chrome
// bumpers with overriders, small round tail lamps in the kicked-up tail and twin
// exhausts. Rounded masses are scaled spheres (blobs), never rotated, so the body
// reads voluptuous rather than slab-sided. Body panels carry the "paint" recolour
// slot (iconic Silver Birch); `drive` spins the wire wheels and `hazards` blinks
// the four indicators.

const PAINT = "#b6bcc0"; // Silver Birch — the "paint" slot default
const CHROME = "#dee2e6"; // bright chrome — bumpers, grille rim, wire rims (reads over the silver)
const TRIM = "#17181b"; // gloss-black trim (pillars, valances, vent backing)
const GRILLE = "#232529"; // dark interior of the grille mouth
const WELL = "#141518"; // wheel-well discs
const GLASS = "#26333f"; // tinted (dark) side / rear glazing
const SCREEN = "#37505f"; // windscreen (a shade lighter than the tint)
const CABIN = "#121418"; // near-black interior block behind the glass
const COVER = "#93b0c2"; // clear glass headlamp cover (faired lamps)
const TIRE = "#1a1b1d";
const SPOKE = "#c7ccd1"; // bright wire spokes / rim
const HUB = "#b7bcc2";
const LAMP = "#eef4f8"; // headlamp glass
const RED = "#a51525"; // tail lamps
const REDDARK = "#6f1019"; // tail-lamp base
const AMBER = "#e8a13a";
const INT = "#8a6b3f"; // tan interior hint through the glass
const PLATE = "#eceff2";

// Spec-sheet skeleton (metres). Long front overhang (front axle back under the
// long bonnet), short rear overhang — the classic GT proportion.
const WHEEL_R = 0.33;
const TYRE_W = 0.185;
const WHEEL_X = 0.78; // half track — tyre outer face proud of the body side
const AXLE_F = 1.05;
const AXLE_R = -1.44; // wheelbase 2.49
const BODY_X = 0.80; // main body half-width
const OUTER = WHEEL_X + TYRE_W / 2; // tyre outer cap ~0.8725 (proud of the well)

// The unbroken fastback roofline: a short formal roof, then three overlapping
// tilted paint boxes whose top surfaces meet on shared breakpoints, so the roof
// peak falls to a short kicked-up tail in ONE continuous line — NO flat deck.
// Each box's forward top corner meets the roof rear corner (or the previous
// segment) and NEVER rises above it, so there is no raised-deck hump.
// Top-surface breakpoints (z, y): (-0.35, 1.30) -> (-1.05, 1.00)
// -> (-1.70, 0.80) -> (-2.12, 0.70). Each next segment is shallower (Kamm tail).
const SEG1_ANGLE = 0.4049; // steep rear-window drop, atan(0.30/0.70)
const SEG2_ANGLE = 0.2984; // mid fastback, atan(0.20/0.65)
const SEG3_ANGLE = 0.2340; // flatter — the short kicked-up tail, atan(0.10/0.42)
const BONNET_TILT = 0.09; // long bonnet, front lower toward the grille
const SCREEN_RAKE = 0.6; // curved wraparound windscreen

function box(
  w: number, h: number, d: number, color: string,
  position: Vec3, rotation?: Vec3, extra?: Partial<Part>,
): Part {
  return { geometry: new THREE.BoxGeometry(w, h, d), color, position, rotation, ...extra };
}

function cyl(
  rTop: number, rBot: number, h: number, seg: number, color: string,
  position: Vec3, rotation?: Vec3, extra?: Partial<Part>,
): Part {
  return { geometry: new THREE.CylinderGeometry(rTop, rBot, h, seg), color, position, rotation, ...extra };
}

/** A painted body panel box — carries the "paint" recolour slot. */
function paint(w: number, h: number, d: number, position: Vec3, rotation?: Vec3): Part {
  return box(w, h, d, PAINT, position, rotation, { slot: "paint" });
}

/** A painted rounded mass: a unit sphere scaled per axis, never rotated
 *  (rotation applies after scale and would shear a non-uniform blob). Used for
 *  the curvaceous bonnet, wings, haunches and tail cap. */
function blob(scale: Vec3, position: Vec3): Part {
  return { geometry: new THREE.SphereGeometry(1, 16, 12), color: PAINT, position, scale, slot: "paint" };
}

/** One chrome wire wheel: dark tyre, a bright rim ring, twelve thin bright
 *  spokes proud of the tyre's outer cap, a chrome hub and a knock-off spinner
 *  cone. Every part shares `name` so the `drive` track spins them as one node
 *  about the axle. `side` is +1 for the right (+x) wheel, -1 for the left. */
function wheel(side: 1 | -1, z: number, name: string): Part[] {
  const x = side * WHEEL_X;
  const y = WHEEL_R;
  const parts: Part[] = [
    cyl(WHEEL_R, WHEEL_R, TYRE_W, 20, TIRE, [x, y, z], [0, 0, Math.PI / 2], { name }),
    {
      geometry: new THREE.TorusGeometry(0.29, 0.024, 6, 22),
      color: SPOKE,
      position: [side * (OUTER + 0.012), y, z] as Vec3,
      rotation: [0, Math.PI / 2, 0] as Vec3,
      name,
    },
  ];
  for (let k = 0; k < 12; k++) {
    parts.push(
      box(0.016, 0.56, 0.016, SPOKE, [side * (OUTER + 0.008), y, z], [(k * Math.PI) / 12, 0, 0], { name }),
    );
  }
  parts.push(cyl(0.075, 0.075, 0.05, 14, HUB, [side * (OUTER + 0.02), y, z], [0, 0, Math.PI / 2], { name }));
  parts.push({ // knock-off spinner cone pointing outward
    geometry: new THREE.ConeGeometry(0.05, 0.09, 12),
    color: CHROME,
    position: [side * (OUTER + 0.06), y, z] as Vec3,
    rotation: [0, 0, (-side * Math.PI) / 2] as Vec3,
    name,
  });
  return parts;
}

/** The opened wheel well: a dark disc proud of the body side plus a slim
 *  body-coloured brow (an upper half-torus over the tyre). The [0, pi/2, 0] yaw
 *  puts the 0..pi arc exactly over the top half; the tyre's outer cap stays
 *  proud of both so the wheel is never swallowed. */
function arch(side: 1 | -1, z: number): Part[] {
  return [
    cyl(0.4, 0.4, 0.04, 20, WELL, [side * BODY_X, WHEEL_R, z], [0, 0, Math.PI / 2]),
    {
      geometry: new THREE.TorusGeometry(0.39, 0.03, 6, 20, Math.PI),
      color: PAINT,
      position: [side * (BODY_X + 0.02), WHEEL_R, z] as Vec3,
      rotation: [0, Math.PI / 2, 0] as Vec3,
      slot: "paint",
    },
  ];
}

// The four hazard indicators — parts and blink tracks are generated from one
// table so the pivots (each part's own centre) can never drift from the parts.
// Fronts are amber chips inboard of the faired headlamps; rears are amber chips
// beside the round tail lamps.
const INDICATORS: ReadonlyArray<{ name: string; pos: Vec3; size: Vec3 }> = [
  { name: "indicator-fl", pos: [-0.44, 0.5, 2.16], size: [0.1, 0.05, 0.04] },
  { name: "indicator-fr", pos: [0.44, 0.5, 2.16], size: [0.1, 0.05, 0.04] },
  { name: "indicator-rl", pos: [-0.42, 0.58, -2.16], size: [0.09, 0.05, 0.04] },
  { name: "indicator-rr", pos: [0.42, 0.58, -2.16], size: [0.09, 0.05, 0.04] },
];

function parts(): Part[] {
  const P: Part[] = [];
  const S = [1, -1] as const;

  // -- Running gear ----------------------------------------------------------
  P.push(
    ...wheel(-1, AXLE_F, "wheel-fl"), ...wheel(1, AXLE_F, "wheel-fr"),
    ...wheel(-1, AXLE_R, "wheel-rl"), ...wheel(1, AXLE_R, "wheel-rr"),
  );
  for (const z of [AXLE_F, AXLE_R]) {
    P.push(cyl(0.05, 0.05, 1.5, 8, TRIM, [0, WHEEL_R, z], [0, 0, Math.PI / 2])); // axle
    for (const s of S) P.push(...arch(s, z));
  }
  P.push(box(1.5, 0.12, 4.1, TRIM, [0, 0.26, -0.05])); // underbody pan

  // -- Lower body (paint) -----------------------------------------------------
  // A full-length sill-to-waist tub kept LOW, plus a waist/shoulder band, so the
  // roof and the continuous fastback boxes are always the topmost surfaces
  // (never a flat deck peeking above them).
  P.push(paint(1.58, 0.34, 4.2, [0, 0.49, -0.05])); // main tub, sill to waist (top 0.66)
  P.push(paint(1.56, 0.18, 3.2, [0, 0.75, -0.3])); // waist/shoulder band under the greenhouse (top 0.84)
  // Rounded rear haunches — the DB5 hip over the rear wheels.
  for (const s of S) P.push(blob([0.14, 0.24, 0.8], [s * 0.72, 0.56, -1.44]));

  // -- The long rounded bonnet (paint) ----------------------------------------
  // Rounded front wings flank a long bonnet core, sloping gently down to the
  // grille. The wings host the faired headlamps; their z-span stops at the nose
  // (no forward spikes). They are the widest point up front.
  for (const s of S) P.push(blob([0.19, 0.16, 0.85], [s * 0.62, 0.62, 1.42]));
  P.push(paint(1.26, 0.18, 2.0, [0, 0.82, 1.25], [BONNET_TILT, 0, 0])); // long bonnet core
  P.push(blob([0.28, 0.055, 0.95], [0, 0.87, 1.3])); // subtle central power-bulge ridge
  P.push(paint(0.5, 0.045, 0.34, [0, 0.9, 1.92], [BONNET_TILT, 0, 0])); // subtle bonnet-scoop hint near the nose

  // -- Nose and tail caps (paint) ---------------------------------------------
  P.push(paint(1.42, 0.42, 0.22, [0, 0.62, 2.09])); // nose block behind the grille (front face z ~2.20)
  P.push(paint(1.44, 0.4, 0.22, [0, 0.5, -2.1])); // short kicked-up tail panel
  P.push(blob([0.68, 0.08, 0.14], [0, 0.68, -2.06])); // rounded tail lip where the fastback lands
  // Fill under the bumpers, back to the wells (no see-through in profile).
  P.push(box(1.24, 0.2, 0.28, TRIM, [0, 0.32, 2.04])); // front valance
  P.push(box(1.24, 0.24, 0.3, TRIM, [0, 0.35, -2.0])); // rear valance

  // -- The unbroken fastback: three overlapping tilted boxes -------------------
  // Top-surface breakpoints (z, y): (-0.35,1.30) -> (-1.05,1.00) -> (-1.70,0.80)
  // -> (-2.12,0.70). Each box's forward top corner is computed to land exactly
  // on the roof rear corner / previous segment (see the ANGLE derivations) and
  // NEVER rise above it — so the roof peak falls to the short Kamm tail in ONE
  // continuous line with no raised-deck hump. Widths grow toward the rear
  // haunches; the boxes are opaque silver, with only a small backlight inset.
  P.push(paint(1.44, 0.46, 0.78, [0, 0.9386, -0.609], [-SEG1_ANGLE, 0, 0])); // sail: steep rear-window drop
  P.push(paint(1.5, 0.34, 0.72, [0, 0.7375, -1.325], [-SEG2_ANGLE, 0, 0])); // mid fastback
  P.push(paint(1.54, 0.26, 0.52, [0, 0.6235, -1.88], [-SEG3_ANGLE, 0, 0])); // tail deck onto the Kamm tail
  P.push(cyl(0.03, 0.03, 0.016, 12, CHROME, [0, 0.647, -1.86], [-SEG3_ANGLE, 0, 0])); // tail badge disc

  // -- Greenhouse: a low, formal GT glasshouse ---------------------------------
  // A raked wraparound windscreen, a SHORT formal roof at the peak, side glass
  // set INBOARD of the roof edge (never past it, so no "ears"), and a modest
  // dark backlight lying ON the silver sail (an inset window, not a dark wall).
  // Roof half-width 0.63; nothing in the glazing exceeds it.
  P.push(box(1.12, 0.03, 0.1, TRIM, [0, 0.83, 0.62])); // cowl vent at the windscreen base
  P.push(box(1.16, 0.28, 1.0, CABIN, [0, 0.94, -0.05])); // dark interior block behind the glass (top 1.08)
  P.push(box(0.94, 0.05, 0.5, INT, [0, 0.85, 0.15])); // tan interior hint through the glass
  P.push(box(1.16, 0.48, 0.05, SCREEN, [0, 1.05, 0.475], [-SCREEN_RAKE, 0, 0])); // wraparound windscreen
  for (const s of S) P.push(paint(0.09, 0.48, 0.08, [s * 0.55, 1.02, 0.49], [-SCREEN_RAKE, 0, 0])); // thin A-pillars, inboard
  // A domed formal roof tapered narrower than the body (tumblehome) so the
  // greenhouse reads as a rounded cap, not a flat slab bridging the body sides.
  P.push(paint(1.16, 0.07, 0.72, [0, 1.26, -0.005])); // short formal roof, the peak of the car
  P.push(blob([0.62, 0.05, 0.4], [0, 1.25, -0.02])); // domed roof crown, a touch proud of the flat cap
  P.push(box(1.06, 0.03, 0.55, GLASS, [0, 1.163, -0.692], [-SEG1_ANGLE, 0, 0])); // dark backlight inset on the sail
  for (const s of S) {
    P.push(box(0.035, 0.36, 0.66, GLASS, [s * 0.58, 1.0, 0.16], [0, 0, s * 0.2])); // door glass, tumblehome inboard
    P.push(box(0.05, 0.34, 0.06, TRIM, [s * 0.58, 0.99, -0.18], [0, 0, s * 0.2])); // B-pillar
    P.push(box(0.035, 0.26, 0.42, GLASS, [s * 0.585, 0.98, -0.42], [0, 0, s * 0.2])); // rear quarter glass
    P.push(box(0.02, 0.022, 1.5, CHROME, [s * 0.615, 0.83, -0.15])); // chrome belt strip under the glass
  }

  // -- Front fascia: the iconic wide, low, trapezoidal Aston grille -----------
  // A big proud chrome-framed mouth: a dark backing, a chrome surround wider at
  // the top than the bottom, and fine horizontal chrome slats narrowing downward
  // — the whole grille reads low and WIDE, wider at the top, and is the clear
  // face of the car (all elements stand proud of the nose face at z=2.20).
  P.push(box(1.06, 0.42, 0.03, GRILLE, [0, 0.62, 2.205])); // dark grille backing, just proud of the nose
  P.push(box(1.12, 0.05, 0.05, CHROME, [0, 0.83, 2.23])); // top rim (widest)
  P.push(box(0.92, 0.05, 0.05, CHROME, [0, 0.41, 2.23])); // bottom rim (narrower)
  for (const s of S) P.push(box(0.05, 0.44, 0.05, CHROME, [s * 0.52, 0.62, 2.23], [0, 0, s * 0.12])); // angled sides (top wider)
  for (let i = 0; i < 6; i++) {
    const w = 1.02 - i * 0.03; // slats narrow toward the bottom to follow the trapezoid
    P.push(box(w, 0.024, 0.04, CHROME, [0, 0.79 - i * 0.072, 2.222])); // fine horizontal chrome slat
  }
  P.push(cyl(0.055, 0.055, 0.02, 16, CHROME, [0, 0.83, 2.25], [Math.PI / 2, 0, 0])); // grille-top badge disc

  // Faired headlamps on the rounded wings: chrome bezel, lamp glass, then a clear
  // glass cover flush with the wing (the covered-lamp look).
  for (const s of S) {
    P.push({
      geometry: new THREE.CylinderGeometry(0.11, 0.11, 0.03, 18),
      color: CHROME, position: [s * 0.64, 0.69, 1.82] as Vec3,
      rotation: [Math.PI / 2, 0, 0] as Vec3, scale: [1, 0.9, 1] as Vec3,
    }); // bezel
    P.push({
      geometry: new THREE.CylinderGeometry(0.09, 0.09, 0.02, 18),
      color: LAMP, position: [s * 0.64, 0.69, 1.835] as Vec3,
      rotation: [Math.PI / 2, 0, 0] as Vec3, scale: [1, 0.9, 1] as Vec3,
    }); // lamp glass
    P.push({
      geometry: new THREE.SphereGeometry(0.11, 14, 10),
      color: COVER, position: [s * 0.64, 0.685, 1.85] as Vec3,
      scale: [1, 0.86, 0.42] as Vec3,
    }); // faired glass cover, flush on the wing front
  }
  // Slim chrome front bumper: a blade with two overriders.
  P.push(box(1.32, 0.08, 0.09, CHROME, [0, 0.4, 2.23]));
  for (const s of S) P.push(box(0.09, 0.2, 0.12, CHROME, [s * 0.42, 0.45, 2.25])); // overrider bombs
  P.push(box(0.34, 0.11, 0.02, PLATE, [0, 0.3, 2.25])); // front plate below the grille

  // -- Front-wing side strakes: three chrome vents behind each front wheel -----
  for (const s of S) {
    P.push(box(0.03, 0.2, 0.24, TRIM, [s * 0.795, 0.68, 0.72])); // dark vent backing
    for (let i = 0; i < 3; i++) {
      P.push(box(0.05, 0.03, 0.2, CHROME, [s * 0.81, 0.75 - i * 0.06, 0.72])); // horizontal chrome strake
    }
  }

  // -- Rear fascia: small round tail lamps + slim chrome bumper ----------------
  for (const s of S) {
    P.push(cyl(0.07, 0.07, 0.04, 14, CHROME, [s * 0.5, 0.6, -2.17], [Math.PI / 2, 0, 0])); // bezel
    P.push(cyl(0.055, 0.055, 0.05, 14, REDDARK, [s * 0.5, 0.6, -2.18], [Math.PI / 2, 0, 0])); // base
    P.push(cyl(0.045, 0.045, 0.03, 14, RED, [s * 0.5, 0.6, -2.2], [Math.PI / 2, 0, 0])); // red lens
  }
  P.push(box(1.32, 0.08, 0.09, CHROME, [0, 0.42, -2.17])); // rear bumper blade
  for (const s of S) P.push(box(0.09, 0.2, 0.12, CHROME, [s * 0.42, 0.47, -2.19])); // overrider bombs
  P.push(box(0.34, 0.11, 0.02, PLATE, [0, 0.4, -2.21])); // rear plate
  // Twin chrome exhaust tips under the tail.
  for (const s of S) P.push(cyl(0.032, 0.032, 0.16, 12, CHROME, [s * 0.28, 0.28, -2.21], [Math.PI / 2, 0, 0]));

  // -- Furniture --------------------------------------------------------------
  for (const s of S) {
    P.push(box(0.05, 0.02, 0.02, TRIM, [s * 0.71, 0.92, 0.28])); // mirror arm
    P.push(cyl(0.04, 0.04, 0.02, 12, CHROME, [s * 0.745, 0.94, 0.28], [0, 0, Math.PI / 2])); // mirror head
    P.push(box(0.02, 0.028, 0.11, CHROME, [s * 0.72, 0.72, -0.4])); // door handle
    P.push(box(0.26, 0.012, 0.03, TRIM, [s * 0.24, 0.85, 0.5], [-SCREEN_RAKE, 0, 0])); // wiper
  }
  for (const ind of INDICATORS) {
    P.push(box(ind.size[0], ind.size[1], ind.size[2], AMBER, ind.pos, undefined, { name: ind.name }));
  }

  return P;
}

// One wheel revolution per cycle; positive x-rotation rolls the car toward +Z.
const WHEEL_PIVOTS: ReadonlyArray<{ name: string; pivot: Vec3 }> = [
  { name: "wheel-fl", pivot: [-WHEEL_X, WHEEL_R, AXLE_F] },
  { name: "wheel-fr", pivot: [WHEEL_X, WHEEL_R, AXLE_F] },
  { name: "wheel-rl", pivot: [-WHEEL_X, WHEEL_R, AXLE_R] },
  { name: "wheel-rr", pivot: [WHEEL_X, WHEEL_R, AXLE_R] },
];

export const astonMartinDb5 = defineModel({
  id: "aston-martin-db5",
  name: "Aston Martin DB5",
  batchId: "cars",
  tags: ["car", "aston-martin", "db5", "coupe", "grand-tourer", "classic"],
  summary: "A 1963 Aston Martin DB5 grand-tourer coupe at true scale, in Silver Birch.",
  description:
    "An Aston Martin DB5 (1963) grand-tourer coupe at real 1 unit = 1 metre " +
    "scale (about 4.57 long, 1.68 wide, 1.32 tall, 2.49 wheelbase, on chrome " +
    "wire wheels). A long, low, voluptuous silver coupe whose side is one " +
    "continuous low line: a long curved bonnet with a subtle power bulge and " +
    "scoop hint, a curved wraparound windscreen, and a gently curved formal " +
    "roof flowing in an unbroken fastback down to a short kicked-up (Kamm) " +
    "tail — a closed body-colour roof over dark glass, never an open deck. The " +
    "signature cues are all present: the iconic wide, low, chrome-rimmed " +
    "trapezoidal Aston grille with fine horizontal chrome slats, faired-in " +
    "covered headlamps under glass on the rounded front wings, three chrome " +
    "side strakes on each front wing behind the wheel, chrome wire wheels with " +
    "knock-off spinners, slim chrome bumpers with overriders, small round tail " +
    "lamps, twin exhausts and a tan interior hint through the glass. Body " +
    "panels carry the 'paint' recolour slot (Silver Birch); `drive` spins the " +
    "wire wheels and `hazards` blinks the four indicators.",
  author: "Fable 5",
  footprint: 2.44,
  parts,
  animations: [
    {
      id: "drive",
      label: "Drive",
      duration: 1,
      tracks: WHEEL_PIVOTS.map(({ name, pivot }) => ({
        target: name,
        pivot,
        rotation: [
          { t: 0, value: [0, 0, 0] as Vec3 },
          { t: 0.5, value: [Math.PI, 0, 0] as Vec3 },
          { t: 1, value: [2 * Math.PI, 0, 0] as Vec3 },
        ],
      })),
    },
    {
      id: "hazards",
      label: "Hazard lights",
      duration: 1,
      tracks: INDICATORS.map(({ name, pos }) => ({
        target: name,
        pivot: pos,
        scale: [
          { t: 0, value: 1 },
          { t: 0.42, value: 1 },
          { t: 0.5, value: 0.02 },
          { t: 0.92, value: 0.02 },
          { t: 1, value: 1 },
        ],
      })),
    },
  ],
});
